import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { pageVariants, pageTransition, fadeUpItem } from '../../components/PageTransition';
import PolicySidebar from './components/PolicySidebar';
import PolicySection from './components/PolicySection';
import MobileNavMenu from './components/MobileNavMenu';

const SECTION_IDS = [
  'overview',
  'data-collected',
  'how-we-use',
  'data-retention',
  'third-party',
  'user-rights',
  'cookies',
  'security',
  'contact',
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries?.forEach((entry) => {
          if (entry?.isIntersecting) {
            setActiveSection(entry?.target?.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    SECTION_IDS?.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef?.current?.observe(el);
    });

    return () => observerRef?.current?.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="min-h-screen bg-background"
    >
      <Helmet>
        <title>Privacy Policy - Prime One Plus</title>
        <meta name="description" content="Prime One Plus Privacy Policy: Learn how we collect, use, and protect your health and wellness data." />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Page Header */}
        <motion.div
          variants={fadeUpItem}
          initial="initial"
          animate="animate"
          className="mb-10"
        >
          <button
            onClick={() => navigate('/profile-settings')}
            className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Profile Settings</span>
          </button>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-xl">
                  <Icon name="Shield" size={20} color="white" />
                </div>
                <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
              </div>
              <p className="text-muted-foreground max-w-xl">
                We believe in full transparency about how your health data is handled. This document explains exactly what we collect, why, and how you stay in control.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
              <span className="text-xs text-muted-foreground">Last updated</span>
              <span className="text-sm font-medium text-foreground">March 9, 2026</span>
              <div className="flex items-center space-x-1.5 mt-1">
                <div className="w-2 h-2 rounded-full bg-success"></div>
                <span className="text-xs text-success font-medium">GDPR & CCPA Compliant</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Compliance Badges */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10"
        >
          {[
            { icon: 'Shield', label: 'HIPAA Compliant', color: 'bg-success/10 border-success/20 text-success' },
            { icon: 'Lock', label: 'End-to-End Encrypted', color: 'bg-accent/10 border-accent/20 text-accent' },
            { icon: 'Globe', label: 'GDPR Ready', color: 'bg-primary/10 border-primary/20 text-primary' },
            { icon: 'FileCheck', label: 'CCPA Compliant', color: 'bg-warning/10 border-warning/20 text-warning' },
          ]?.map((badge) => (
            <div key={badge?.label} className={`flex items-center space-x-2 px-3 py-2.5 rounded-xl border ${badge?.color}`}>
              <Icon name={badge?.icon} size={15} />
              <span className="text-xs font-medium">{badge?.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Two-column layout */}
        <div className="flex gap-8">
          {/* Sidebar — desktop only */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <PolicySidebar activeSection={activeSection} onSectionClick={scrollToSection} />
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-6 min-w-0">

            {/* 1. Overview */}
            <PolicySection
              id="overview"
              icon="FileText"
              iconBg="bg-primary"
              title="Overview"
              subtitle="Who we are and what this policy covers"
            >
              <p>
                Prime One Plus ("we", "our", or "us") is a wellness tracking platform designed to help you monitor, understand, and improve your health. This Privacy Policy applies to all data processed through our web application at <strong className="text-foreground">vitaltrack7676.builtwithrocket.new</strong> and any related services.
              </p>
              <p>
                By using Prime One Plus, you agree to the collection and use of information as described in this policy. We are committed to protecting your personal health data and will never sell it to third parties.
              </p>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-2">
                <p className="text-foreground font-medium text-sm mb-1">Our Core Commitment</p>
                <p>Your health data belongs to you. We act as a custodian — processing it only to deliver the service you signed up for, improve your experience, and keep the platform secure.</p>
              </div>
            </PolicySection>

            {/* 2. Data We Collect */}
            <PolicySection
              id="data-collected"
              icon="Database"
              iconBg="bg-accent"
              title="Data We Collect"
              subtitle="A complete list of every data category we process"
            >
              <p>We collect data in two ways: information you provide directly and data generated automatically as you use the platform.</p>

              <div className="space-y-4 mt-2">
                {[
                  {
                    icon: 'Dumbbell',
                    title: 'Workout & Activity Logs',
                    items: [
                      'Exercise type, duration, sets, reps, and weight',
                      'Step counts and daily movement data',
                      'Calorie burn estimates',
                      'Workout timestamps and frequency patterns',
                    ],
                  },
                  {
                    icon: 'Utensils',
                    title: 'Nutrition Tracking',
                    items: [
                      'Meal logs including food items and portion sizes',
                      'Macronutrient and calorie intake per meal',
                      'Dietary preferences and restrictions you set',
                      'Meal timing and eating patterns',
                    ],
                  },
                  {
                    icon: 'Moon',
                    title: 'Sleep Patterns',
                    items: [
                      'Sleep start and end times',
                      'Sleep duration and quality ratings',
                      'Sleep stage data (if provided)',
                      'Sleep goal progress over time',
                    ],
                  },
                  {
                    icon: 'Droplets',
                    title: 'Hydration Records',
                    items: [
                      'Daily water intake logs (volume and timestamps)',
                      'Hydration goal settings and completion rates',
                    ],
                  },
                  {
                    icon: 'Users',
                    title: 'Social Interactions',
                    items: [
                      'Friend connections and squad memberships',
                      'Duel participation, results, and proof photos',
                      'Activity feed interactions (likes, comments)',
                      'Leaderboard rankings and challenge history',
                    ],
                  },
                  {
                    icon: 'Monitor',
                    title: 'Device & Technical Information',
                    items: [
                      'Browser type, version, and operating system',
                      'IP address and approximate geographic location',
                      'Device identifiers and screen resolution',
                      'Session duration and page interaction events',
                    ],
                  },
                  {
                    icon: 'User',
                    title: 'Account & Profile Data',
                    items: [
                      'Name, email address, and profile photo',
                      'Date of birth, height, weight, and fitness goals',
                      'Notification preferences and privacy settings',
                      'Authentication credentials (stored as hashed values)',
                    ],
                  },
                ]?.map((category) => (
                  <div key={category?.title} className="bg-muted/40 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name={category?.icon} size={16} className="text-primary" />
                      <span className="font-semibold text-foreground text-sm">{category?.title}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {category?.items?.map((item) => (
                        <li key={item} className="flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </PolicySection>

            {/* 3. How We Use Data */}
            <PolicySection
              id="how-we-use"
              icon="Settings"
              iconBg="bg-warning"
              title="How We Use Your Data"
              subtitle="The specific purposes for which we process your information"
            >
              <p>We process your data only for the purposes listed below. We do not use your health data for advertising, profiling for third parties, or any purpose beyond what is described here.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {[
                  { icon: 'BarChart2', title: 'Deliver Core Features', desc: 'Power your dashboards, progress reports, activity tracking, nutrition logs, sleep analysis, and social features.' },
                  { icon: 'Bell', title: 'Notifications & Reminders', desc: 'Send push notifications, email digests, duel alerts, and wellness reminders based on your preferences.' },
                  { icon: 'TrendingUp', title: 'Personalized Insights', desc: 'Generate wellness insights, goal recommendations, and achievement milestones tailored to your data.' },
                  { icon: 'ShieldCheck', title: 'Security & Fraud Prevention', desc: 'Detect unauthorized access, prevent abuse, and maintain the integrity of your account.' },
                  { icon: 'Wrench', title: 'Platform Improvement', desc: 'Analyze anonymized usage patterns to fix bugs, improve performance, and develop new features.' },
                  { icon: 'Mail', title: 'Service Communications', desc: 'Send transactional emails such as account confirmations, password resets, and important policy updates.' },
                ]?.map((use) => (
                  <div key={use?.title} className="bg-muted/40 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name={use?.icon} size={15} className="text-warning" />
                      <span className="font-semibold text-foreground text-sm">{use?.title}</span>
                    </div>
                    <p className="text-xs">{use?.desc}</p>
                  </div>
                ))}
              </div>
            </PolicySection>

            {/* 4. Data Retention */}
            <PolicySection
              id="data-retention"
              icon="Clock"
              iconBg="bg-secondary"
              title="Data Retention"
              subtitle="How long we keep your data and why"
            >
              <p>We retain your data only as long as necessary to provide the service or as required by law. Below is a breakdown of our retention schedule by data category.</p>
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2.5 pr-4 font-semibold text-foreground">Data Category</th>
                      <th className="text-left py-2.5 pr-4 font-semibold text-foreground">Retention Period</th>
                      <th className="text-left py-2.5 font-semibold text-foreground">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {[
                      ['Account & Profile Data', 'Until account deletion', 'Required to provide the service'],
                      ['Workout & Activity Logs', '7 years', 'Long-term health trend analysis'],
                      ['Nutrition Logs', '7 years', 'Dietary pattern tracking'],
                      ['Sleep Records', '7 years', 'Sleep health monitoring'],
                      ['Hydration Logs', '2 years', 'Short-term habit tracking'],
                      ['Social Interactions', '3 years after last activity', 'Community integrity'],
                      ['Device & Technical Logs', '90 days', 'Security and debugging'],
                      ['Email Communications', '3 years', 'Legal compliance'],
                      ['Deleted Account Data', '30 days (grace period)', 'Account recovery window'],
                    ]?.map(([category, period, reason]) => (
                      <tr key={category}>
                        <td className="py-2.5 pr-4 text-foreground font-medium">{category}</td>
                        <td className="py-2.5 pr-4 text-primary font-medium">{period}</td>
                        <td className="py-2.5">{reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-muted/40 rounded-xl p-4 mt-2">
                <p><strong className="text-foreground">After account deletion:</strong> We begin a 30-day grace period during which your account can be restored. After 30 days, all personal data is permanently and irreversibly deleted from our systems and backups within 90 days.</p>
              </div>
            </PolicySection>

            {/* 5. Third-Party Services */}
            <PolicySection
              id="third-party"
              icon="Link"
              iconBg="bg-accent"
              title="Third-Party Services"
              subtitle="External providers we use and what data they access"
            >
              <p>We use a minimal set of carefully selected third-party services. We do not sell your data to any third party. Each provider is bound by a Data Processing Agreement (DPA) and is only permitted to use your data to provide the specific service described.</p>
              <div className="space-y-4 mt-2">
                <div className="bg-muted/40 rounded-xl p-5">
                  <div className="flex items-start space-x-3">
                    <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Database" size={18} className="text-accent" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-semibold text-foreground">Supabase</span>
                        <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">Database & Auth</span>
                      </div>
                      <p className="mb-2">Supabase is our primary backend infrastructure provider. It stores all your health data, manages authentication, and handles real-time data synchronization.</p>
                      <ul className="space-y-1">
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Data stored:</strong> All health logs, profile data, social interactions, and authentication credentials</span></li>
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Location:</strong> Data centers in the United States (AWS us-east-1)</span></li>
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Security:</strong> Row-Level Security (RLS) policies ensure users can only access their own data</span></li>
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Privacy policy:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">supabase.com/privacy</a></span></li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/40 rounded-xl p-5">
                  <div className="flex items-start space-x-3">
                    <div className="w-9 h-9 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Mail" size={18} className="text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-semibold text-foreground">Resend</span>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Email Delivery</span>
                      </div>
                      <p className="mb-2">Resend handles all transactional email delivery including account confirmations, password resets, wellness digests, duel notifications, and squad alerts.</p>
                      <ul className="space-y-1">
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Data shared:</strong> Your email address and the content of notifications you've opted into</span></li>
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Purpose:</strong> Transactional email delivery only — no marketing without explicit consent</span></li>
                        <li className="flex items-start space-x-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></span><span><strong className="text-foreground">Privacy policy:</strong> <a href="https://resend.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com/privacy</a></span></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-4 mt-2">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={15} className="text-warning mt-0.5 flex-shrink-0" />
                  <p><strong className="text-foreground">No advertising networks.</strong> We do not use Google Ads, Facebook Pixel, or any advertising tracking technology. Your health data is never used for ad targeting.</p>
                </div>
              </div>
            </PolicySection>

            {/* 6. User Rights */}
            <PolicySection
              id="user-rights"
              icon="UserCheck"
              iconBg="bg-success"
              title="Your Rights"
              subtitle="What you can request and how to exercise your rights"
            >
              <p>Depending on your location, you have specific legal rights regarding your personal data. We honor these rights for all users regardless of jurisdiction.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {[
                  {
                    icon: 'Eye',
                    title: 'Right to Access',
                    desc: 'Request a complete copy of all personal data we hold about you, including health logs, profile data, and activity history.',
                    action: 'Export from Profile Settings → Privacy & Data',
                  },
                  {
                    icon: 'Edit',
                    title: 'Right to Rectification',
                    desc: 'Correct inaccurate or incomplete personal data at any time through your profile settings.',
                    action: 'Edit in Profile Settings → Personal Info',
                  },
                  {
                    icon: 'Trash2',
                    title: 'Right to Erasure',
                    desc: 'Request permanent deletion of your account and all associated data. We will process this within 30 days.',
                    action: 'Profile Settings → Privacy & Data → Delete Account',
                  },
                  {
                    icon: 'Download',
                    title: 'Right to Portability',
                    desc: 'Download your data in a machine-readable JSON format to transfer to another service.',
                    action: 'Profile Settings → Privacy & Data → Export Data',
                  },
                  {
                    icon: 'Ban',
                    title: 'Right to Object',
                    desc: 'Opt out of analytics data collection, marketing communications, and anonymous data sharing at any time.',
                    action: 'Profile Settings → Privacy & Data → Toggle settings',
                  },
                  {
                    icon: 'Pause',
                    title: 'Right to Restrict Processing',
                    desc: 'Request that we limit how we use your data while a dispute or review is in progress.',
                    action: 'Contact us at privacy@primeoneplusapp.com',
                  },
                ]?.map((right) => (
                  <div key={right?.title} className="bg-muted/40 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name={right?.icon} size={15} className="text-success" />
                      <span className="font-semibold text-foreground text-sm">{right?.title}</span>
                    </div>
                    <p className="text-xs mb-2">{right?.desc}</p>
                    <div className="flex items-center space-x-1.5 bg-success/10 rounded-lg px-2.5 py-1.5">
                      <Icon name="ArrowRight" size={12} className="text-success" />
                      <span className="text-xs text-success font-medium">{right?.action}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-muted/40 rounded-xl p-4 mt-2">
                <p className="font-semibold text-foreground text-sm mb-1">Response Times</p>
                <p>We respond to all data rights requests within <strong className="text-foreground">30 days</strong>. For complex requests, we may extend this by an additional 60 days and will notify you. We do not charge fees for exercising your rights.</p>
              </div>
            </PolicySection>

            {/* 7. Cookies */}
            <PolicySection
              id="cookies"
              icon="Cookie"
              iconBg="bg-warning"
              title="Cookies & Tracking"
              subtitle="What cookies we use and how to manage them"
            >
              <p>We use a minimal set of cookies and local storage to make the application work correctly. We do not use third-party tracking cookies or advertising cookies.</p>
              <div className="space-y-3 mt-2">
                {[
                  {
                    type: 'Essential',
                    required: true,
                    color: 'bg-success/10 border-success/20',
                    badgeColor: 'bg-success/20 text-success',
                    items: [
                      { name: 'sb-auth-token', purpose: 'Supabase authentication session token', duration: 'Session / 1 week (if "Remember me" enabled)' },
                      { name: 'theme-preference', purpose: 'Stores your light/dark mode preference', duration: 'Persistent (1 year)' },
                      { name: 'color-scheme', purpose: 'Stores your selected color scheme', duration: 'Persistent (1 year)' },
                    ],
                  },
                  {
                    type: 'Analytics (Optional)',
                    required: false,
                    color: 'bg-warning/10 border-warning/20',
                    badgeColor: 'bg-warning/20 text-warning',
                    items: [
                      { name: 'analytics_session', purpose: 'Anonymous usage analytics to improve the platform', duration: '30 days' },
                    ],
                  },
                ]?.map((group) => (
                  <div key={group?.type} className={`rounded-xl p-4 border ${group?.color}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-foreground text-sm">{group?.type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${group?.badgeColor}`}>
                        {group?.required ? 'Always Active' : 'Opt-in'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {group?.items?.map((item) => (
                        <div key={item?.name} className="text-xs">
                          <span className="font-mono font-semibold text-foreground">{item?.name}</span>
                          <span className="text-muted-foreground"> — {item?.purpose}</span>
                          <span className="text-muted-foreground/70"> ({item?.duration})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2">You can manage analytics cookies in <strong className="text-foreground">Profile Settings → Privacy & Data</strong>. Essential cookies cannot be disabled as they are required for the application to function.</p>
            </PolicySection>

            {/* 8. Security */}
            <PolicySection
              id="security"
              icon="Shield"
              iconBg="bg-primary"
              title="Security"
              subtitle="How we protect your data"
            >
              <p>We implement industry-standard security measures to protect your health data from unauthorized access, disclosure, alteration, or destruction.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {[
                  { icon: 'Lock', title: 'Encryption at Rest', desc: 'All data stored in Supabase is encrypted using AES-256 encryption.' },
                  { icon: 'Wifi', title: 'Encryption in Transit', desc: 'All data transmitted between your browser and our servers uses TLS 1.3.' },
                  { icon: 'Key', title: 'Secure Authentication', desc: 'Passwords are hashed using bcrypt. We support secure session management with automatic expiry.' },
                  { icon: 'Shield', title: 'Row-Level Security', desc: 'Database-level policies ensure each user can only access their own data, even if our application layer is compromised.' },
                  { icon: 'Eye', title: 'Access Controls', desc: 'Only authorized personnel with a legitimate need can access production systems, and all access is logged.' },
                  { icon: 'RefreshCw', title: 'Regular Backups', desc: 'Automated daily backups with point-in-time recovery to prevent data loss.' },
                ]?.map((measure) => (
                  <div key={measure?.title} className="flex items-start space-x-3 bg-muted/40 rounded-xl p-4">
                    <Icon name={measure?.icon} size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-foreground text-sm block mb-1">{measure?.title}</span>
                      <p className="text-xs">{measure?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-muted/40 rounded-xl p-4 mt-2">
                <p className="font-semibold text-foreground text-sm mb-1">Security Incident Response</p>
                <p>In the event of a data breach that affects your personal data, we will notify you within <strong className="text-foreground">72 hours</strong> of becoming aware of the incident, as required by GDPR Article 33. Notifications will be sent to your registered email address.</p>
              </div>
            </PolicySection>

            {/* 9. Contact */}
            <PolicySection
              id="contact"
              icon="Mail"
              iconBg="bg-accent"
              title="Contact Us"
              subtitle="How to reach us with privacy questions or requests"
            >
              <p>If you have questions about this Privacy Policy, want to exercise your data rights, or need to report a privacy concern, please contact us through the following channels.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="bg-muted/40 rounded-xl p-5">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="Mail" size={16} className="text-accent" />
                    <span className="font-semibold text-foreground text-sm">Privacy Requests</span>
                  </div>
                  <p className="text-xs mb-3">For data access, deletion, export, or any privacy-related request:</p>
                  <a href="mailto:privacy@primeoneplusapp.com" className="text-primary hover:underline font-medium text-sm">privacy@primeoneplusapp.com</a>
                  <p className="text-xs text-muted-foreground mt-2">Response time: within 30 days</p>
                </div>
                <div className="bg-muted/40 rounded-xl p-5">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="AlertTriangle" size={16} className="text-warning" />
                    <span className="font-semibold text-foreground text-sm">Security Issues</span>
                  </div>
                  <p className="text-xs mb-3">To report a security vulnerability or suspected data breach:</p>
                  <a href="mailto:security@primeoneplusapp.com" className="text-primary hover:underline font-medium text-sm">security@primeoneplusapp.com</a>
                  <p className="text-xs text-muted-foreground mt-2">We take all reports seriously and respond within 24 hours</p>
                </div>
              </div>
              <div className="bg-muted/40 rounded-xl p-4 mt-2">
                <p className="font-semibold text-foreground text-sm mb-2">Supervisory Authority</p>
                <p>If you are located in the European Economic Area and believe we have not adequately addressed your privacy concerns, you have the right to lodge a complaint with your local data protection authority. A list of EU data protection authorities is available at <a href="https://edpb.europa.eu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">edpb.europa.eu</a>.</p>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-2">
                <p className="text-xs text-center text-muted-foreground">
                  This Privacy Policy was last updated on <strong className="text-foreground">March 9, 2026</strong>. We will notify you of material changes via email and an in-app notification at least 30 days before they take effect.
                </p>
              </div>
            </PolicySection>

          </div>
        </div>
      </main>
      {/* Mobile floating nav */}
      <MobileNavMenu activeSection={activeSection} onSectionClick={scrollToSection} />
    </motion.div>
  );
};

export default PrivacyPolicy;
